import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { environment } from 'environments/environment';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { GenerateLinkSuccessMssgeComponent } from '../generate-link-success-mssge/generate-link-success-mssge.component';
import { GenerateQuizLinkPayload } from '@edtech-ui/core/models/admin-quiz-enhancement/post-generate-link';
import { ToastrService } from 'ngx-toastr';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { StudentJobApplicantNameValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-name';

@Component({
	selector: 'app-publish-with-link-dialog-box',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, FormsModule, MatProgressSpinnerModule, MatTooltipModule,
		HasPermissionDirective
	],
	templateUrl: './publish-with-link-dialog-box.component.html',
	styleUrl: './publish-with-link-dialog-box.component.scss'
})
export class PublishWithLinkDialogBoxComponent implements OnInit {

	quizForm: FormGroup;
	selectedOption: string = 'invite';
	searchText: string = '';
	minDateTime: string = '';
	isDropdownOpen = false;
	selectedCourseId: number | null = null;
	selectedGroups: number[] = [];
	selectedOptions: string[] = [];
	invitedEmailList: string[] = [];
	shuffleQuestions: boolean = false;
	shuffleOptions: boolean = false;
	isGroupDropdownOpen = false;
	isSettingsExpanded = false;
	quizEditForm: FormGroup;
	totalQuiz: number = 0;
	classroomId = environment.classroomId;
	subscription = new Subscription();
	isLoading = false;
	isPublishedAsMockTest = false;
	selectedLogoFile: File | null = null;
	logoPreview: string | null = null;
	PermissionForStudents = PermissionForStudents;



	constructor(
		private dialogRef: MatDialogRef<PublishWithLinkDialogBoxComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { quizId: string, fromParent?: boolean },
		private quizService: QuizManagementService,
		private dialog: MatDialog,
		private toastr: ToastrService
	) {
		this.quizForm = new FormGroup({
			link_name: new FormControl('', [Validators.required]),
			invited_emails: new FormControl(''),
			start_date: new FormControl(this.getCurrentDateTime(), [
				this.noPastDateValidator()]),
			end_date: new FormControl('', Validators.required),
			expire_in_hours: new FormControl('24 h'),
			specific_candidates: new FormControl('invite'),
			groupIds: new FormArray([]),
			use_default_settings: new FormControl(true),
			company_name: new FormControl('', StudentJobApplicantNameValidator()),
			role_name: new FormControl('', StudentJobApplicantNameValidator()),
			category_name: new FormControl(''),
			test_category: new FormControl('')
		},
			{
				validators: [this.dateRangeValidator()]
			});

		this.quizEditForm = new FormGroup({
			quiz_name: new FormControl(''),
			description: new FormControl(''),
			time_limit: new FormControl(0),
			hh: new FormControl(0),
			mm: new FormControl(0),
			ss: new FormControl(0),
			attempts_allowed: new FormControl(1, [Validators.required, Validators.min(1)]),
			passing_score: new FormControl(0),
			questions_per_quiz: new FormControl(0, [Validators.required, Validators.min(1)]),
			shuffle_questions: new FormControl(false),
			shuffle_options: new FormControl(false),
			isProctored: new FormControl(false)
		});
	}


	ngOnInit() {
		if (this.data?.quizId) {
			this.getQuizById(this.data.quizId);
		}
		this.quizForm.valueChanges.subscribe(() => {
			this.calculateExpiry();
		});

		this.quizForm.get('specific_candidates')?.valueChanges.subscribe(value => {
			if (value !== 'independent') {
				this.groupIds.clear();
			}
		});

		this.quizForm.get('use_default_settings')?.valueChanges.subscribe((isDefault: boolean) => {
			this.isSettingsExpanded = !isDefault;
		});

		this.minDateTime = this.getCurrentDateTime();

		document.addEventListener('click', this.handleOutsideClick.bind(this));
	}



	handleOutsideClick(event: any) {
		const clickedInside =
			event.target.closest('.dropdown') ||
			event.target.closest('.dropdown-panel') ||
			event.target.closest('.dropdown-display');

		if (!clickedInside) {
			this.isDropdownOpen = false;
		}
	}

	private parseDateTimeLocal(value: string): Date {
		const [date, time] = value.split('T');
		return new Date(`${date}T${time}:00`);
	}


	calculateExpiry() {
		const start = this.quizForm.get('start_date')?.value;
		const end = this.quizForm.get('end_date')?.value;

		if (!start || !end) {
			this.quizForm.get('expire_in_hours')?.setValue('24 h', { emitEvent: false });
			return;
		}

		const start_date = this.parseDateTimeLocal(start);
		const end_date = this.parseDateTimeLocal(end);

		const diffMs = end_date.getTime() - start_date.getTime();

		if (diffMs <= 0) {
			this.quizForm.get('expire_in_hours')?.setValue('0 h', { emitEvent: false });
			return;
		}

		const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
		this.quizForm.get('expire_in_hours')?.setValue(`${diffHours} h`, { emitEvent: false });
	}


	closeDialog() {
		this.dialogRef.close();
	}


	toggleQuizOption(option: string) {
		this.selectedOption = option;
	}


	groups = [
		{ id: 1, name: 'Engineering Team' },
		{ id: 2, name: 'Design Team' },
		{ id: 3, name: 'QA Squad' },
		{ id: 4, name: 'Product Managers' }
	];


	get groupIds(): FormArray {
		return this.quizForm.get('groupIds') as FormArray;
	}


	getGroupName(id: number): string {
		const group = this.groups.find(g => g.id === id);
		return group ? group.name : '';
	}


	toggleGroupDropdown() {
		this.isGroupDropdownOpen = !this.isGroupDropdownOpen;
	}


	toggleDropdown() {
		this.isDropdownOpen = !this.isDropdownOpen;
	}


	onSelectGroup(group: number) {
		if (this.selectedGroups.includes(group)) {
			this.selectedGroups = this.selectedGroups.filter(g => g !== group);
		} else {
			this.selectedGroups.push(group);
		}
	}


	proctorOptions: string[] = [
		'Detect tab switch',
		'Restrict new tabs / window switch',
		'Enable camera access',
		'Face detection',
		'Multiple person detection',
		'No talking detection',
		'Eye movement tracking'
	];


	toggleOption(option: string) {
		if (this.selectedOptions.includes(option)) {
			this.selectedOptions = this.selectedOptions.filter(o => o !== option);
		} else {
			this.selectedOptions.push(option);
		}
	}


	getQuizById(quizId: string) {
		this.subscription.add(
			this.quizService.getQuizById(quizId).subscribe({
				next: (res) => {
					if (res?.Success && res.Data) {
						const quiz = res.Data;
						const t = this.secondsToHMS(quiz.time_limit || 0);
						this.selectedCourseId = quiz.course_id;
						this.quizEditForm.patchValue({
							quiz_name: quiz.quiz_name,
							description: quiz.description || '',
							hh: t.hh, mm: t.mm, ss: t.ss,
							attempts_allowed: quiz.attempts_allowed,
							passing_score: quiz.passing_score,
							questions_per_quiz: quiz.questions_per_quiz,
							shuffle_questions: quiz.shuffle_questions,
							shuffle_options: quiz.shuffle_options,
							mode: quiz.mode,
							isProctored: quiz.proctoring_config?.enable_proctoring || false
						});
						this.selectedGroups = quiz.assigned_groups || [];
						this.selectedOptions = this.mapProctoring(quiz.proctoring_config);
					}
				}
			})
		);
	}



	mapProctoring(config: any): string[] {
		if (!config) return [];
		const map: any = {
			detect_tab_switch: 'Detect tab switch',
			restrict_new_tabs: 'Restrict new tabs / window switch',
			enable_camera: 'Enable camera access',
			face_detection: 'Face detection',
			multiple_person_detection: 'Multiple person detection',
			no_talking_detection: 'No talking detection',
			eye_movement_tracking: 'Eye movement tracking'
		};
		return Object.keys(config).filter(key => config[key] === true && map[key]).map(key => map[key]);
	}



	buildProctoringPayload() {
		const selected = this.selectedOptions || [];
		const isProctored = this.quizEditForm.get('isProctored')?.value;
		return {
			enable_proctoring: isProctored,
			detect_tab_switch: selected.includes('Detect tab switch'),
			restrict_new_tabs: selected.includes('Restrict new tabs / window switch'),
			enable_camera: selected.includes('Enable camera access'),
			face_detection: selected.includes('Face detection'),
			multiple_person_detection: selected.includes('Multiple person detection'),
			no_talking_detection: selected.includes('No talking detection'),
			eye_movement_tracking: selected.includes('Eye movement tracking'),
			max_warnings: 3
		};
	}


	private secondsToHMS(totalSeconds: number) {
		const hh = Math.floor(totalSeconds / 3600);
		const mm = Math.floor((totalSeconds % 3600) / 60);
		const ss = totalSeconds % 60;
		return { hh, mm, ss };
	}


	private hmsToSeconds(hh: number, mm: number, ss: number): number {
		return (Number(hh || 0) * 3600) + (Number(mm || 0) * 60) + Number(ss || 0);
	}


	onGroupSelect(event: any) {
		const value = Number(event.target.value);
		if (value && !this.selectedGroups.includes(value)) {
			this.selectedGroups.push(value);
			this.groupIds.push(new FormControl(value));
		}
		event.target.value = '';
	}


	removeGroup(group: number) {
		this.selectedGroups = this.selectedGroups.filter(g => g !== group);
	}


	onCheckboxChange(id: number, event: any) {
		if (event.target.checked) {
			if (!this.selectedGroups.includes(id)) {
				this.selectedGroups.push(id);
				this.groupIds.push(new FormControl(id));
			}
		} else {
			this.removeGroup(id);
		}
	}


	get filteredGroups() {
		return this.groups.filter(g => g.name.toLowerCase().includes(this.searchText.toLowerCase()));
	}


	selectCourse(courseId: number) {
		this.selectedCourseId = courseId;
		this.isDropdownOpen = false;
		this.quizForm.patchValue({
			course_id: courseId
		});
	}


	getSelectedCourseName() {
		const course = this.filteredGroups.find(g => g.id === this.selectedCourseId);
		return course ? course.name : '';
	}


	handleTimeInput(event: any, max: number) {
		let value = event.target.value;
		value = value.replace(/[^0-9]/g, '');

		if (value.length > 2) {
			value = value.slice(0, 2);
		}
		if (value !== '' && Number(value) > max) {
			value = max.toString();
		}
		event.target.value = value;
	}


	getCurrentDateTime(): string {
		const now = new Date();
		const offset = now.getTimezoneOffset();
		const localDate = new Date(now.getTime() - offset * 60000);
		return localDate.toISOString().slice(0, 16);
	}


	noPastDateValidator() {
		return (control: any) => {
			if (!control.value) return null;
			const selected = new Date(control.value);
			const now = new Date();
			now.setSeconds(0, 0);
			return selected < now ? { pastDate: true } : null;
		};
	}


	togglePublishAsMockTest(event: any) {
		this.isPublishedAsMockTest = event.target.checked;
		const endDateControl = this.quizForm.get('end_date');
		const companyNameControl = this.quizForm.get('company_name');
		const roleNameControl = this.quizForm.get('role_name');
		const testCategoryControl = this.quizForm.get('test_category');
		const attemptsControl = this.quizEditForm.get('attempts_allowed');

		if (this.isPublishedAsMockTest) {
			endDateControl?.clearValidators();
			endDateControl?.updateValueAndValidity();

			companyNameControl?.setValidators(Validators.compose([Validators.required, StudentJobApplicantNameValidator()]));
			roleNameControl?.setValidators(Validators.compose([Validators.required, StudentJobApplicantNameValidator()]));
			testCategoryControl?.setValidators([Validators.required]);
			attemptsControl?.clearValidators();
			attemptsControl?.setValue(null);
		} else {
			endDateControl?.setValidators([Validators.required]);
			endDateControl?.updateValueAndValidity();

			companyNameControl?.setValidators(StudentJobApplicantNameValidator());
			roleNameControl?.setValidators(StudentJobApplicantNameValidator());
			testCategoryControl?.clearValidators();
			attemptsControl?.setValidators([Validators.required, Validators.min(1)]);
			attemptsControl?.setValue(null);
		}

		companyNameControl?.updateValueAndValidity();
		roleNameControl?.updateValueAndValidity();
		testCategoryControl?.updateValueAndValidity();
		attemptsControl?.updateValueAndValidity();
	}


	postGenerativeLink() {
		const form = this.quizForm.value;
		const quiz = this.quizEditForm.value;

		const payload: GenerateQuizLinkPayload = {
			link_name: form.link_name || 'default-link',
			audience_type: form.specific_candidates,
			invited_emails: this.invitedEmailList,
			assigned_groups: this.selectedGroups || [],
			start_date: form.start_date
				? new Date(form.start_date).toISOString()
				: new Date().toISOString(),
			end_date: form.end_date
				? new Date(form.end_date).toISOString()
				: (this.isPublishedAsMockTest ? new Date(Date.now() + 50 * 365 * 24 * 60 * 60 * 1000).toISOString() : new Date().toISOString()),
			expire_in_hours: parseInt(form.expire_in_hours, 10) || 24,
			use_default_settings: form.use_default_settings,
			time_limit: this.hmsToSeconds(quiz.hh, quiz.mm, quiz.ss),
			attempts_allowed: this.isPublishedAsMockTest ? null : (quiz.attempts_allowed !== null && quiz.attempts_allowed !== '' ? Number(quiz.attempts_allowed) : null),
			passing_score: Number(quiz.passing_score) || 0,
			questions_per_quiz: Number(quiz.questions_per_quiz) || 0,
			shuffle_questions: quiz.shuffle_questions,
			shuffle_options: quiz.shuffle_options,
			proctoring_config: this.buildProctoringPayload(),
			is_public: this.isPublishedAsMockTest,
			company_name: this.isPublishedAsMockTest ? form.company_name : null,
			role_name: this.isPublishedAsMockTest ? form.role_name : null,
			test_category: this.isPublishedAsMockTest ? form.test_category : null
		}

		const formData = new FormData();
		formData.append('request_dto', JSON.stringify(payload));

		if (this.selectedLogoFile) {
			formData.append('company_logo', this.selectedLogoFile);
		}
		// if (this.invitedEmailList.length === 0) {
		// 	this.toastr.warning('At least one email recipient is required');
		// 	return;
		// }

		console.log('np FormData Payload:');

		formData.forEach((value, key) => {
			console.log(key, value);
		}); this.isLoading = true;
		this.subscription.add(
			this.quizService.postGenerateLink(this.data.quizId, formData).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Message);
						const successDialogBox = this.dialog.open(GenerateLinkSuccessMssgeComponent, {
							data: {
								url: response.Data.url,
								fromParent: this.data?.fromParent
							}
						});
						successDialogBox.afterClosed().subscribe(() => {
							this.dialogRef.close(true);
						})
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		);
	}


	addEmails() {
		const value = this.quizForm.get('invited_emails')?.value || '';
		const emails = value.split(/[\s,]+/).map((e: string) => e.trim()).filter((e: string) => e);
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const validEmails = emails.filter((email: string) => emailRegex.test(email));
		const invalidEmails = emails.filter((email: string) => !emailRegex.test(email));
		if (invalidEmails.length > 0) {
			this.toastr.error(`Invalid email(s): ${invalidEmails.join(', ')}`);
			return;
		}
		this.invitedEmailList = [...new Set([...this.invitedEmailList, ...validEmails]),];
		this.quizForm.get('invited_emails')?.setValue('');
	}


	removeEmail(email: string) {
		this.invitedEmailList = this.invitedEmailList.filter(e => e !== email);
	}



	get isFormValid(): boolean {
		let isValid = this.quizForm.valid;
		if (this.quizForm.get('use_default_settings')?.value === false) {
			isValid = isValid && this.quizEditForm.valid;
		}
		if (this.isPublishedAsMockTest) {
			isValid = isValid && !!this.selectedLogoFile;
		}
		return isValid && !this.isLoading;
	}


	dateRangeValidator() {
		return (group: AbstractControl): ValidationErrors | null => {

			const start = group.get('start_date')?.value;
			const end = group.get('end_date')?.value;

			if (!start || !end) {
				return null;
			}

			const startDate = new Date(start);
			const endDate = new Date(end);
			const diffMs = endDate.getTime() - startDate.getTime();
			const minDiff = 2 * 60 * 1000;

			if (diffMs < minDiff) {
				return {
					invalidDateRange: true
				};
			}
			return null;
		};
	}


	onCompanyLogoSelected(event: Event): void {
		const input = event.target as HTMLInputElement;

		if (!input.files || input.files.length === 0) {
			return;
		}
		const file = input.files[0];
		this.selectedLogoFile = file;
		const reader = new FileReader();
		reader.onload = () => { this.logoPreview = reader.result as string; };
		reader.readAsDataURL(file);
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}